/* eslint-disable jsx-a11y/no-autofocus -- every `autoFocus` in this file sits
   inside a focus-trapped overlay the user just opened on purpose, which is
   where moving focus is correct. The rule exists for page-load focus jumps —
   focus yanked somewhere the person never asked to go — and it cannot tell the
   two apart. */
import { Command } from 'cmdk';
import { Dialog, VisuallyHidden } from 'radix-ui';
import type { LucideIcon } from 'lucide-react';
import { Search } from 'lucide-react';
import { type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Kbd } from './display';
import { useReturnFocus } from './overlay';

/* ============================================================================
   The command palette — one keystroke to anything.

   This is the answer to a product that had three separate list screens and no
   way to search across them: ⌘K searches every chart, dashboard and data source
   by name, and runs the handful of actions worth reaching without a mouse.
   ========================================================================== */

export interface CommandItem {
  id: string;
  label: string;
  /** Extra words that should match this item (type, source name, id). */
  keywords?: string[];
  hint?: string;
  icon?: LucideIcon;
  shortcut?: string;
  onSelect: () => void;
}

export interface CommandGroup {
  heading: string;
  items: CommandItem[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  groups: CommandGroup[];
  placeholder?: string;
  /** Shown when nothing matches — with the query, so it reads as an answer. */
  emptyMessage?: ReactNode;
  loading?: boolean;
}

export function CommandPalette({
  open,
  onOpenChange,
  query,
  onQueryChange,
  groups,
  placeholder = 'Search charts, dashboards, data sources… or run a command',
  emptyMessage,
  loading,
}: CommandPaletteProps) {
  const nonEmpty = groups.filter((g) => g.items.length > 0);
  // ⌘K is pressed from wherever the user was working; put them back there.
  const returnFocus = useReturnFocus();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          onOpenAutoFocus={returnFocus.onOpenAutoFocus}
          onCloseAutoFocus={returnFocus.onCloseAutoFocus}
          className={cn(
            'fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2',
            'overflow-hidden rounded-xl border border-border bg-surface-raised shadow-overlay',
            'data-[state=open]:animate-scale-in',
          )}
        >
          <VisuallyHidden.Root>
            <Dialog.Title>Search and commands</Dialog.Title>
          </VisuallyHidden.Root>
          <Command
            loop
            // Filtering happens here (client-side, over already-loaded lists) so
            // typing never waits on a request.
            className="flex flex-col"
          >
            <div className="flex items-center gap-2 border-b border-border-subtle px-3">
              <Search className="size-4 shrink-0 text-fg-faint" aria-hidden />
              <Command.Input
                autoFocus
                value={query}
                onValueChange={onQueryChange}
                placeholder={placeholder}
                className="h-11 w-full bg-transparent text-sm text-fg outline-none placeholder:text-fg-faint"
              />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="scroll-region max-h-[min(24rem,60dvh)] p-1.5">
              {loading && (
                <div className="px-3 py-6 text-center text-xs text-fg-muted">Loading your workspace…</div>
              )}
              <Command.Empty className="px-3 py-8 text-center text-xs text-fg-muted">
                {emptyMessage ?? (
                  <>
                    Nothing matches <span className="font-medium text-fg-secondary">“{query}”</span>.
                  </>
                )}
              </Command.Empty>
              {nonEmpty.map((group) => (
                <Command.Group
                  key={group.heading}
                  heading={group.heading}
                  className={cn(
                    '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2',
                    '[&_[cmdk-group-heading]]:text-[0.625rem] [&_[cmdk-group-heading]]:font-semibold',
                    '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider',
                    '[&_[cmdk-group-heading]]:text-fg-faint',
                  )}
                >
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Command.Item
                        key={item.id}
                        value={[item.label, ...(item.keywords ?? [])].join(' ')}
                        onSelect={() => {
                          item.onSelect();
                          onOpenChange(false);
                        }}
                        className={cn(
                          'flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-[0.8125rem] text-fg-secondary',
                          'data-[selected=true]:bg-surface-hover data-[selected=true]:text-fg',
                        )}
                      >
                        {Icon && <Icon className="size-4 shrink-0 text-fg-faint" aria-hidden />}
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {item.hint && <span className="shrink-0 text-xs text-fg-faint">{item.hint}</span>}
                        {item.shortcut && <Kbd>{item.shortcut}</Kbd>}
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
